/**
 * Human labels for recorded actors (`CreatedBy`, `UpdatedBy`, audit "Who").
 *
 * The SAME resolution the Edit History drawer uses (R3-F5): the signed-in
 * user from their own JWT claims (no request), everyone else through ONE
 * cached identity user-list read (`useHistoryActors`, shared with Users &
 * Roles — never per actor), automated principals (`…-bot`, `migration:…`) as
 * a translated "System", unknown opaque ids in their short form.
 */
import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue';
import { useI18n } from 'vue-i18n';
import { getActivePinia } from 'pinia';

import { isOpaqueUserId, type HistoryActor } from '@/api/services/audit';
import { useAuthStore } from '@/stores/auth';
import { describeActor, useHistoryActors } from '@/composables/useEntityHistory';

/**
 * The signed-in user as an actor, from their own claims (no request). A host
 * without Pinia (isolated tests) simply has no `self`.
 * @returns The user's id + display, or null.
 */
export function useSelfActor(): ComputedRef<({ id: string } & HistoryActor) | null> {
  const auth = getActivePinia() ? useAuthStore() : null;
  return computed(() => {
    const c = auth?.claims;
    if (!c?.sub) return null;
    const name = (c.name ?? [c.given_name, c.family_name].filter(Boolean).join(' ')).trim();
    return { id: c.sub, name, email: c.email ?? '' };
  });
}

/**
 * Resolve recorded actors to display labels.
 * @param employerId The employer whose users to look up (reactive).
 * @param rawActors The recorded actor values on screen (reactive; blanks ignored).
 * @returns `actorLabel(raw)` — name / email / "System" / short id / raw text ('' for blank).
 */
export function useActorLabels(
  employerId: MaybeRefOrGetter<string | null | undefined>,
  rawActors: MaybeRefOrGetter<readonly (string | null | undefined)[]>,
) {
  const { t } = useI18n();
  const self = useSelfActor();
  const ids = computed(() => [
    ...new Set(
      toValue(rawActors)
        .map((a) => (a ?? '').trim())
        .filter((a) => isOpaqueUserId(a)),
    ),
  ]);
  const { actors } = useHistoryActors(employerId, ids, self);

  /**
   * @param raw The recorded actor.
   * @returns Its display label.
   */
  function actorLabel(raw: string | null | undefined): string {
    const view = describeActor(raw ?? '', actors.value);
    switch (view.kind) {
      case 'empty':
        return '';
      case 'system':
        return t('v2.ui.edit-history.system-actor');
      default:
        return view.text;
    }
  }

  return { actorLabel, actors };
}
